import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import jsQR from 'jsqr';

/**
 * Lector de códigos QR con la cámara del dispositivo.
 *
 * Nació para la **tarjeta de lealtad de Google Wallet** (2026-08-29): su QR lleva
 * el teléfono del cliente a 10 dígitos en texto plano, que es exactamente la
 * llave con la que el POS lo busca. Escanear = teclear el número, sin errores de
 * dedo y sin pedirle al cliente que lo dicte.
 *
 * **Dos decodificadores, en este orden:**
 * ① `BarcodeDetector`, nativo del navegador — es el que corre en las tablets
 *    (Chrome/Android) y no cuesta un solo byte de bundle.
 * ② `jsQR` sobre un canvas — el respaldo para los navegadores que no lo traen
 *    (Chrome de escritorio en Windows, Firefox, Safari), donde si no el botón
 *    aparecería y no serviría para nada.
 *
 * ⚠️ **La cámara se apaga SIEMPRE al salir.** Un stream que queda vivo deja el
 * led encendido y el dispositivo caliente; por eso el `stop` está en el cleanup
 * del efecto y no colgado del botón de cerrar, que no se pulsa cuando el modal
 * se desmonta por otra razón.
 *
 * ⚠️ Requiere **contexto seguro** (HTTPS o localhost). Firebase Hosting lo es.
 */

interface QrScannerProps {
    open: boolean;
    onClose: () => void;
    /** Se llama UNA vez con el texto crudo del código; el modal se cierra solo. */
    onScan: (value: string) => void;
    title?: string;
    helperText?: string;
}

type ScannerState = 'starting' | 'scanning' | 'error';

/** `BarcodeDetector` no está en los tipos del DOM todavía. */
const getBarcodeDetector = (): any => {
    const detector = (window as any).BarcodeDetector;
    if (!detector) return undefined;
    try {
        return new detector({ formats: ['qr_code'] });
    } catch {
        return undefined;
    }
};

const getCameraErrorMessage = (error: any): string => {
    const name = error?.name || '';
    if (name === 'NotAllowedError' || name === 'SecurityError') {
        return 'No se dio permiso para usar la cámara. Actívalo en el candado de la barra de direcciones y vuelve a intentar.';
    }
    if (name === 'NotFoundError' || name === 'OverconstrainedError') {
        return 'Este dispositivo no tiene una cámara disponible.';
    }
    if (name === 'NotReadableError') {
        return 'La cámara está ocupada por otra aplicación.';
    }
    return 'No se pudo abrir la cámara. Captura el número a mano.';
};

export const QrScanner: React.FunctionComponent<QrScannerProps> = (props) => {
    const { open, onClose, onScan, title, helperText } = props;
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [state, setState] = useState<ScannerState>('starting');
    const [errorMessage, setErrorMessage] = useState<string>('');

    // El resultado se entrega UNA sola vez: el loop corre a ~30 fps y el mismo
    // código se detecta en muchos cuadros seguidos.
    const alreadyScanned = useRef<boolean>(false);

    const handleResult = useCallback(
        (value: string) => {
            if (alreadyScanned.current) return;
            alreadyScanned.current = true;
            onScan(value);
            onClose();
        },
        [onScan, onClose]
    );

    useEffect(() => {
        if (!open) return;

        alreadyScanned.current = false;
        setState('starting');
        setErrorMessage('');

        let stream: MediaStream | undefined;
        let frameId: number | undefined;
        let cancelled = false;
        const detector = getBarcodeDetector();

        const scanFrame = async () => {
            const video = videoRef.current;
            if (cancelled || !video || video.readyState !== video.HAVE_ENOUGH_DATA) {
                frameId = requestAnimationFrame(scanFrame);
                return;
            }
            try {
                if (detector) {
                    const codes = await detector.detect(video);
                    if (codes?.length && codes[0].rawValue) {
                        handleResult(String(codes[0].rawValue));
                        return;
                    }
                } else {
                    const canvas = canvasRef.current;
                    const context = canvas?.getContext('2d', { willReadFrequently: true });
                    if (canvas && context) {
                        canvas.width = video.videoWidth;
                        canvas.height = video.videoHeight;
                        context.drawImage(video, 0, 0, canvas.width, canvas.height);
                        const image = context.getImageData(0, 0, canvas.width, canvas.height);
                        const code = jsQR(image.data, image.width, image.height, { inversionAttempts: 'dontInvert' });
                        if (code?.data) {
                            handleResult(code.data);
                            return;
                        }
                    }
                }
            } catch {
                // Un cuadro que no se pudo leer no es un error del escáner: sigue.
            }
            if (!cancelled) frameId = requestAnimationFrame(scanFrame);
        };

        const start = async () => {
            try {
                if (!navigator.mediaDevices?.getUserMedia) {
                    throw new Error('unsupported');
                }
                // `environment` = cámara trasera en la tablet; en una laptop se
                // ignora y usa la única que hay.
                stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: 'environment' }
                });
                if (cancelled) {
                    stream.getTracks().forEach((track) => track.stop());
                    return;
                }
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    await videoRef.current.play();
                }
                setState('scanning');
                frameId = requestAnimationFrame(scanFrame);
            } catch (error) {
                if (cancelled) return;
                setErrorMessage(getCameraErrorMessage(error));
                setState('error');
            }
        };

        void start();

        return () => {
            cancelled = true;
            if (frameId !== undefined) cancelAnimationFrame(frameId);
            stream?.getTracks().forEach((track) => track.stop());
            if (videoRef.current) videoRef.current.srcObject = null;
        };
    }, [open, handleResult]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xs"
            PaperProps={{ style: { borderRadius: 15, padding: 4 } }}
        >
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight={600}>
                        {title || 'Escanear código'}
                    </Typography>
                    {/* `secondary` es BLANCO PURO en este tema: sobre el papel del
                        diálogo el icono desaparecería. */}
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    {state === 'error' ? (
                        <>
                            <Alert severity="warning">{errorMessage}</Alert>
                            <Button variant="contained" color="primary" onClick={onClose}>
                                Capturar a mano
                            </Button>
                        </>
                    ) : (
                        <>
                            <Box
                                position="relative"
                                borderRadius={2}
                                overflow="hidden"
                                bgcolor="#000"
                                sx={{ aspectRatio: '1 / 1' }}
                            >
                                <video
                                    ref={videoRef}
                                    muted
                                    playsInline
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                                {/* Mira: el cliente acerca el pase hasta que el QR
                                    quede dentro del recuadro. */}
                                <Box
                                    position="absolute"
                                    top="15%"
                                    left="15%"
                                    width="70%"
                                    height="70%"
                                    border="3px solid rgba(255,255,255,0.9)"
                                    borderRadius={2}
                                    sx={{ pointerEvents: 'none' }}
                                />
                                {state === 'starting' && (
                                    <Stack
                                        position="absolute"
                                        top={0}
                                        left={0}
                                        width="100%"
                                        height="100%"
                                        alignItems="center"
                                        justifyContent="center"
                                    >
                                        <CircularProgress color="inherit" sx={{ color: '#FFF' }} />
                                    </Stack>
                                )}
                            </Box>
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                {helperText || 'Coloca el código dentro del recuadro'}
                            </Typography>
                        </>
                    )}
                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                </Stack>
            </DialogContent>
        </Dialog>
    );
};
